import React, { useState, useEffect, useRef } from 'react';
import {
    Settings, Volume2, VolumeX, Type,
    X, RefreshCw, AlertCircle,
    CheckCircle, Wifi, ChevronDown, ChevronUp,
    Pause, Globe, Copy, FileText, Download, Lock, Bot
} from 'lucide-react';
import signalRService from '../services/signalRService';
import { trackException } from '../services/appInsightsService';
import audioService from '../services/audioService';
import Card, { CardHeader, CardBody, CardFooter } from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import './TranslationReceiver-revised.css';
import { renderWithGlossaryHighlights } from '../utils/highlightGlossary';

const LANGUAGES = [
    { code: 'af-ZA', name: 'Afrikaans' },
    { code: 'ar-SA', name: 'Arabic' },
    { code: 'bn-BD', name: 'Bengali' },
    { code: 'bg-BG', name: 'Bulgarian' },
    { code: 'zh-CN', name: 'Chinese Simplified' },
    { code: 'zh-TW', name: 'Chinese Traditional' },
    { code: 'hr-HR', name: 'Croatian' },
    { code: 'cs-CZ', name: 'Czech' },
    { code: 'da-DK', name: 'Danish' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'en-US', name: 'English' },
    { code: 'et-EE', name: 'Estonian' },
    { code: 'fi-FI', name: 'Finnish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'el-GR', name: 'Greek' },
    { code: 'gu-IN', name: 'Gujarati' },
    { code: 'he-IL', name: 'Hebrew' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'hu-HU', name: 'Hungarian' },
    { code: 'is-IS', name: 'Icelandic' },
    { code: 'id-ID', name: 'Indonesian' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'kn-IN', name: 'Kannada' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'lv-LV', name: 'Latvian' },
    { code: 'lt-LT', name: 'Lithuanian' },
    { code: 'ms-MY', name: 'Malay' },
    { code: 'ml-IN', name: 'Malayalam' },
    { code: 'mt-MT', name: 'Maltese' },
    { code: 'mr-IN', name: 'Marathi' },
    { code: 'nb-NO', name: 'Norwegian' },
    { code: 'pl-PL', name: 'Polish' },
    { code: 'pt-PT', name: 'Portuguese' },
    { code: 'pa-IN', name: 'Punjabi' },
    { code: 'ro-RO', name: 'Romanian' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'sr-RS', name: 'Serbian' },
    { code: 'sk-SK', name: 'Slovak' },
    { code: 'sl-SI', name: 'Slovenian' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'sw-KE', name: 'Swahili' },
    { code: 'sv-SE', name: 'Swedish' },
    { code: 'ta-IN', name: 'Tamil' },
    { code: 'te-IN', name: 'Telugu' },
    { code: 'th-TH', name: 'Thai' },
    { code: 'tr-TR', name: 'Turkish' },
    { code: 'uk-UA', name: 'Ukrainian' },
    { code: 'ur-PK', name: 'Urdu' },
    { code: 'vi-VN', name: 'Vietnamese' },
    { code: 'cy-GB', name: 'Welsh' }
];

const TranslationReceiverRevised = ({ isFormHidden = false, setIsFormHidden = () => { } }) => {
    // Form state
    const [sessionId, setSessionId] = useState('');
    const [targetLanguage, setTargetLanguage] = useState('ko-KR');
    const [enableTts, setEnableTts] = useState(false);
    const [showPartial, setShowPartial] = useState(true);
    const [showSourceText, setShowSourceText] = useState(false);
    const [canUnifyModel, setCanUnifyModel] = useState(false);
    const [password, setPassword] = useState('');
    const [autojoin, setAutojoin] = useState(false);
    // Session validation is ALWAYS required - no bypassing allowed for security

    // Connection state
    const [isConnected, setIsConnected] = useState(false);
    const [statusMessage, setStatusMessage] = useState('Enter session details to join');
    const [statusType, setStatusType] = useState('info');
    const [needsAudioPermission, setNeedsAudioPermission] = useState(false);
    const [isIOSDevice, setIsIOSDevice] = useState(false);
    const [sessionNotFound, setSessionNotFound] = useState(false); // Track if session doesn't exist

    // Translation state
    const [translations, setTranslations] = useState([]);
    const [currentPartial, setCurrentPartial] = useState('');
    const [unifiedTranslations, setUnifiedTranslations] = useState([]);
    const [waitingForFinal, setWaitingForFinal] = useState(false);
    const [lastPartialTime, setLastPartialTime] = useState(null);

    const [isSettingsContentCollapsed, setIsSettingsContentCollapsed] = useState(false);
    const [loadingDots, setLoadingDots] = useState('');
    // Speech activity state — driven by SignalR SpeechActivity signal (server-side VAD)
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isSessionPaused, setIsSessionPaused] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [connectionLost, setConnectionLost] = useState(false);
    const [sessionTerminatedByHost, setSessionTerminatedByHost] = useState(false); // Prevent reconnection after host ends session
    const [isSignalRDegraded, setIsSignalRDegraded] = useState(false); // Track SignalR degraded mode (live preview disabled)
    const [showSuccessBanner, setShowSuccessBanner] = useState(true); // Track visibility of success banner

    // Post-session state: when backend returns sessionEnded=true, show transcript/AI viewer instead of live session UI
    const [isPostSession, setIsPostSession] = useState(false);
    const [postSessionData, setPostSessionData] = useState(null);

    // UI preferences
    const [fontSize, setFontSize] = useState(18);
    const [showSettings, setShowSettings] = useState(true);

    // Refs
    const translationOutputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const canAutoScrollRef = useRef(true); // Track if auto-scroll is enabled (user at bottom)

    const connectionHealthCheckRef = useRef(null);
    const lastConnectedSessionIdRef = useRef(null); // Track last connected session ID to preserve history


    // Initialize
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        // Detect iOS
        const iosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
        setIsIOSDevice(iosDevice);

        // Parse URL params
        if (urlParams.get('sessionId')) setSessionId(urlParams.get('sessionId'));
        if (urlParams.get('targetLanguage')) setTargetLanguage(urlParams.get('targetLanguage'));
        if (urlParams.get('enableTts')) setEnableTts(urlParams.get('enableTts') === 'true');
        if (urlParams.get('showPartial')) setShowPartial(urlParams.get('showPartial') === 'true');
        if (urlParams.get('showSourceText')) setShowSourceText(urlParams.get('showSourceText') === 'true');
        if (urlParams.get('canUnifyModel')) setCanUnifyModel(urlParams.get('canUnifyModel') === 'true');
        if (urlParams.get('password')) setPassword(urlParams.get('password'));
        if (urlParams.get('autojoin') === 'true') setAutojoin(true);
        // Session validation is always enforced - removed skipSessionCheck parameter

        // Load preferences
        const savedFontSize = localStorage.getItem('translationFontSize');
        if (savedFontSize) {
            const size = parseInt(savedFontSize);
            setFontSize(Math.min(Math.max(size, 12), 42));
        }

        const savedShowSourceText = localStorage.getItem('showSourceText');
        if (savedShowSourceText) setShowSourceText(savedShowSourceText === 'true');
    }, []);

    // Reset banner visibility when connecting
    useEffect(() => {
        if (isConnected || isReconnecting) {
            setShowSuccessBanner(true);
        }
    }, [isConnected, isReconnecting]);


    // Auto-collapse logic
    useEffect(() => {
        if (isReconnecting || isConnected || connectionLost) return;

        const urlParams = new URLSearchParams(window.location.search);
        const hasUrlSessionId = urlParams.get('sessionId');
        const hasUrlTargetLanguage = urlParams.get('targetLanguage');

        // Don't auto-collapse if session has errors or is in post-session mode
        if (sessionTerminatedByHost || sessionNotFound || isPostSession) {
            setShowSettings(true);
            return;
        }

        if (hasUrlSessionId && hasUrlTargetLanguage &&
            sessionId === hasUrlSessionId && targetLanguage === hasUrlTargetLanguage &&
            !isConnected) {
            setShowSettings(false);
            if (statusType !== 'error') {
                setStatusMessage('Ready to join! Click "Join Session" to connect.');
                setStatusType('info');
            }
        }
    }, [isConnected, statusType, sessionId, targetLanguage, isReconnecting, connectionLost, sessionTerminatedByHost, sessionNotFound, isPostSession]);

    // Auto-join
    useEffect(() => {
        if (autojoin && sessionId && !isConnected && !isReconnecting && !isPostSession) {
            // Only retry periodically if session was terminated by host (might be restarted)
            // Do NOT retry if session doesn't exist (wrong ID won't become right)
            // Do NOT retry if in post-session mode (session is ended, content is available via viewer)
            if (sessionTerminatedByHost && !sessionNotFound) {
                const retryInterval = setInterval(() => {
                    console.log('[Receiver] Auto-checking if terminated session has been restarted...');
                    handleConnect();
                }, 10000); // Check every 10 seconds (less aggressive)

                return () => clearInterval(retryInterval);
            } else if (!sessionTerminatedByHost && !sessionNotFound) {
                // Normal auto-join for new connections (only if session wasn't marked as not found)
                const autoJoinTimer = setTimeout(() => {
                    handleConnect();
                    setShowSettings(false);
                }, 500);
                return () => clearTimeout(autoJoinTimer);
            }
            // If sessionNotFound is true, do nothing - no auto-retry
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autojoin, sessionId, isConnected, isReconnecting, sessionTerminatedByHost, sessionNotFound, isPostSession]);

    // Auto-reconnect on connection loss (network issues)
    useEffect(() => {
        if (connectionLost && !isReconnecting && !sessionTerminatedByHost && !sessionNotFound && !isPostSession) {
            console.log('[Receiver] Connection lost, attempting to reconnect...');
            const reconnectTimer = setTimeout(() => {
                handleConnect();
            }, 3000); // Wait 3 seconds before reconnecting

            return () => clearTimeout(reconnectTimer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectionLost, isReconnecting, sessionTerminatedByHost, sessionNotFound, isPostSession]);

    // Handle scroll position detection (pause auto-scroll when user scrolls up)
    useEffect(() => {
        const handleScroll = () => {
            if (!translationOutputRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = translationOutputRef.current;

            // Calculate distance to bottom
            // Use Math.abs to handle potential rubber-banding/bounce effects on macOS/iOS
            const distanceToBottom = Math.abs(scrollHeight - scrollTop - clientHeight);

            // User is at bottom if within 50px (reduced from 100px to fix "sticky" behavior)
            const isAtBottom = distanceToBottom < 50;
            canAutoScrollRef.current = isAtBottom;
        };

        const container = translationOutputRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Scroll to bottom (only if user is at bottom / auto-scroll enabled)
    useEffect(() => {
        if (canAutoScrollRef.current) {
            // Use requestAnimationFrame for consistent behavior with Host
            requestAnimationFrame(() => {
                if (translationOutputRef.current) {
                    translationOutputRef.current.scrollTop = translationOutputRef.current.scrollHeight;
                }
            });
        }
    }, [translations, currentPartial, unifiedTranslations, loadingDots, isSessionPaused]);

    // Loading dots - driven by SpeechActivity signal when in non-unified, non-partial mode
    // Only animate when server signals speech is active (isSpeaking=true)
    useEffect(() => {
        if (!canUnifyModel && !showPartial && isConnected && isSpeaking) {
            const interval = setInterval(() => {
                setLoadingDots(prev => prev.length >= 3 ? '' : prev + '.');
            }, 500);
            return () => clearInterval(interval);
        } else {
            setLoadingDots('');
        }
    }, [canUnifyModel, showPartial, isConnected, isSpeaking]);

    // Audio management
    useEffect(() => {
        const manageAudioKeepAlive = async () => {
            if (enableTts && isConnected) {
                try {
                    if (!audioService.isInitialized) {
                        await audioService.initialize();
                    }
                    if (audioService.audioContext?.state === 'suspended') {
                        setNeedsAudioPermission(true);
                    }
                } catch (error) {
                    console.error('Failed to manage audio for TTS:', error);
                    setNeedsAudioPermission(true);
                }
            } else if (!enableTts && audioService.keepAudioAlive) {
                audioService.stopSilentAudio();
            }
        };
        manageAudioKeepAlive();
    }, [enableTts, isConnected]);

    // iOS Audio Context
    useEffect(() => {
        if (isIOSDevice && enableTts && isConnected) {
            const maintainAudioContext = async () => {
                if (audioService.audioContext?.state === 'suspended') {
                    try {
                        await audioService.audioContext.resume();
                        if (enableTts && !audioService.keepAudioAlive) {
                            audioService.startSilentAudio();
                        }
                    } catch (error) {
                        console.log('Failed to auto-resume audio context:', error);
                    }
                }
            };

            const events = ['touchstart', 'touchend', 'click', 'scroll'];
            events.forEach(eventType => document.addEventListener(eventType, maintainAudioContext, { passive: true }));
            return () => events.forEach(eventType => document.removeEventListener(eventType, maintainAudioContext));
        }
    }, [isIOSDevice, enableTts, isConnected]);

    // Connection Health
    useEffect(() => {
        if (isConnected && !connectionHealthCheckRef.current) {
            connectionHealthCheckRef.current = setInterval(async () => {
                try {
                    const isHealthy = await signalRService.verifyConnection();
                    if (!isHealthy) {
                        setConnectionLost(true);
                    } else if (connectionLost) {
                        setConnectionLost(false);
                        setIsReconnecting(false);
                    }
                } catch (error) {
                    console.error('Connection health check error:', error);
                }
            }, 15000);
        }
        return () => {
            if (connectionHealthCheckRef.current) {
                clearInterval(connectionHealthCheckRef.current);
                connectionHealthCheckRef.current = null;
            }
        };
    }, [isConnected, connectionLost]);

    // Auto-hide success banner after 5 seconds
    useEffect(() => {
        if (statusType === 'success' && showSuccessBanner) {
            const timer = setTimeout(() => {
                setShowSuccessBanner(false);
            }, 5000);
            return () => clearTimeout(timer);
        } else if (statusType !== 'success') {
            setShowSuccessBanner(true);
        }
    }, [statusType, showSuccessBanner]);


    // Cleanup
    useEffect(() => {
        return () => {
            if (signalRService.isConnected) {
                signalRService.disconnect().catch(console.warn);
            }
        };
    }, []);

    const checkSessionStatus = async () => {
        if (!sessionId) return null;

        try {
            // Check session status via Azure Functions API (anonymous endpoint)
            const response = await fetch(`${signalRService.functionsUrl}translation/get-session-status?sessionId=${encodeURIComponent(sessionId)}`, {
                method: 'GET'
            });

            if (response.ok) {
                let status;
                try {
                    status = await response.json();
                } catch (e) {
                    console.log('[Receiver] Could not parse JSON response from API');
                    return null; // Can't parse, allow connection attempt
                }
                console.log('[Receiver] API session status response:', JSON.stringify(status));

                // Check various response formats from the API

                // If we get an explicit error saying session not found
                if (status && status.error && (status.error.includes('not found') || status.error.includes('does not exist'))) {
                    return { exists: false, error: status.error };
                }

                // If we get status with sessionId, it exists in some form
                if (status && status.sessionId) {
                    // Check for ended session with available post-session content
                    if (status.sessionEnded === true) {
                        return {
                            exists: true,
                            active: false,
                            sessionEnded: true,
                            ...status
                        };
                    }

                    // Status values: 0=WaitingForHost, 1=Active, 2=Closed, 3=Terminated
                    const isActive = status.active === true || status.status === 1;
                    const isWaiting = status.status === 0;
                    const isClosed = status.status === 2 || status.status === 3;

                    return {
                        exists: true,
                        active: isActive,
                        waiting: isWaiting,
                        closed: isClosed,
                        ...status
                    };
                }

                // If status has active field, use that
                if (status && typeof status.active === 'boolean') {
                    return {
                        exists: true,
                        active: status.active,
                        ...status
                    };
                }

                // If we get here with a 200 response, assume session exists but state unknown
                console.log('[Receiver] Got 200 response but unclear format, assuming session exists');
                return { exists: true, active: true }; // Assume active to allow connection attempt
            } else if (response.status === 404) {
                // 404 from pre-check is treated as "can't determine" — not a hard block.
                // The SignalR subscription is the authoritative validator; pre-check can have
                // false negatives when Redis is slow or session was created via a different path.
                console.log('[Receiver] API returned 404 on pre-check - will verify via connection attempt');
                return null;
            } else {
                // Other error status codes - can't determine
                console.log('[Receiver] API returned unexpected status:', response.status);
            }
        } catch (error) {
            console.log('[Receiver] Could not check session status:', error);
            trackException(error, { sessionId, errorType: 'TranslationReceiver_SessionStatusCheckFailed' });
            // If the API endpoint doesn't exist or network error, we can't determine status
            // In this case, allow the connection attempt to proceed
        }

        // Return null if we can't determine status from API
        return null;
    };

    const getAiSummaryUrl = () => {
        const base = process.env.REACT_APP_EXTERNAL_PARTICIPANT_URL || 'https://app-test.qolsync.ai';
        return `${base}/external?sessionId=${encodeURIComponent(sessionId)}&activeTab=ai`;
    };

    const handleConnect = async () => {
        if (!sessionId.trim()) {
            setStatusMessage('Please enter a session ID');
            setStatusType('error');
            return;
        }

        if (isConnected || isReconnecting) return;

        try {
            if (signalRService.isConnected) await signalRService.disconnect();
        } catch (e) { console.warn(e); }

        setIsReconnecting(false);
        setConnectionLost(false);
        setSessionNotFound(false);
        setStatusMessage('Checking session status...');
        setStatusType('info');

        try {
            // Check session status via anonymous API endpoint
            console.log('[Receiver] Checking session status via API for:', sessionId);

            try {
                const sessionStatus = await checkSessionStatus();
                console.log('[Receiver] Session status result:', sessionStatus);

                if (sessionStatus !== null) {
                    // We got a response from the API

                    // Handle ended session with available post-session content
                    if (sessionStatus.sessionEnded === true) {
                        console.log('[Receiver] Session ended but content available - entering post-session mode');
                        setIsPostSession(true);
                        setPostSessionData(sessionStatus);
                        setSessionNotFound(false);
                        setSessionTerminatedByHost(false);
                        setStatusMessage('Session has ended. Transcript and AI features are available below.');
                        setStatusType('info');
                        return; // Skip SignalR connection
                    }

                    if (sessionStatus.exists === false) {
                        // Session definitely doesn't exist - block connection
                        console.log('[Receiver] API confirms session does not exist');
                        setSessionNotFound(true);
                        setSessionTerminatedByHost(false);
                        setStatusMessage(`Session "${sessionId}" does not exist. Please check with your host for the correct session ID.`);
                        setStatusType('error');
                        return;
                    } else if (sessionStatus.waiting === true) {
                        // Session exists but waiting for host
                        console.log('[Receiver] Session is waiting for host to connect');
                        setStatusMessage('Session is waiting for the host to start. Please wait...');
                        setStatusType('warning');
                        return;
                    } else if (sessionStatus.closed === true) {
                        // Session was closed
                        console.log('[Receiver] Session was closed or terminated');
                        setSessionTerminatedByHost(true);
                        setStatusMessage('Session was ended by the host. Please wait for the host to restart the session.');
                        setStatusType('warning');
                        return;
                    }

                    // Session exists and is active or state unknown - proceed
                    console.log('[Receiver] Session check passed, proceeding with connection');
                    setSessionTerminatedByHost(false);
                    setSessionNotFound(false);
                } else {
                    // Could not determine status - allow connection attempt with warning
                    console.log('[Receiver] Could not verify session status - proceeding with caution');
                    setStatusMessage('Session status unclear, attempting connection...');
                    setStatusType('warning');
                }
            } catch (error) {
                console.error('[Receiver] Error checking session status:', error);
                // On API error, allow connection attempt but warn user
                console.warn('[Receiver] Session check failed, attempting connection anyway');
                setStatusMessage('Session verification unavailable, attempting connection...');
                setStatusType('warning');
            }

            // If we get here, either session is active or we couldn't verify - proceed with connection

            setStatusMessage('Connecting...');

            await signalRService.connect(sessionId, targetLanguage, {
                password,
                enableTts,
                showPartial,
                onTranslationReceived: (text, tag, sourceText, isDegraded, durationMs, speaker) => {
                    // Default to S1 if speaker is missing
                    const finalSpeaker = speaker || 'S1';

                    // Final translation received - clear waiting state
                    setWaitingForFinal(false);
                    setLastPartialTime(null);

                    // Update degraded state if SignalR indicates issues
                    if (isDegraded && !isSignalRDegraded) {
                        setIsSignalRDegraded(true);
                        console.log('[Receiver] SignalR entered degraded mode - live preview disabled on server');
                    }

                    // Check if this final was a fallback from partial (tag contains "Fallback-Partial")
                    const isFallback = tag && tag.includes('Fallback-Partial');

                    if (canUnifyModel) {
                        setUnifiedTranslations(prev => {
                            const newTranslations = [...prev];
                            if (newTranslations.length > 0 && !newTranslations[newTranslations.length - 1].isFinal) {
                                newTranslations[newTranslations.length - 1] = {
                                    text,
                                    tag,
                                    timestamp: new Date(),
                                    isFinal: true,
                                    sourceText,
                                    isFallbackFromPartial: isFallback,
                                    durationMs,
                                    speaker: finalSpeaker
                                };
                            } else {
                                newTranslations.push({
                                    text,
                                    tag,
                                    timestamp: new Date(),
                                    isFinal: true,
                                    sourceText,
                                    isFallbackFromPartial: isFallback,
                                    durationMs,
                                    speaker: finalSpeaker
                                });
                            }
                            return newTranslations;
                        });
                    } else {
                        setTranslations(prev => [...prev, {
                            text,
                            tag,
                            timestamp: new Date(),
                            isFinal: true,
                            sourceText,
                            isFallbackFromPartial: isFallback,
                            durationMs,
                            speaker: finalSpeaker
                        }]);
                        setCurrentPartial('');
                    }
                },
                onPartialTextReceived: (text, tag, durationMs, speaker) => {
                    // Default to S1 if speaker is missing
                    const finalSpeaker = speaker || 'S1';

                    // Partial received - expect final soon
                    setWaitingForFinal(true);
                    setLastPartialTime(Date.now());

                    if (canUnifyModel) {
                        setUnifiedTranslations(prev => {
                            const newTranslations = [...prev];
                            if (newTranslations.length > 0 && !newTranslations[newTranslations.length - 1].isFinal) {
                                newTranslations[newTranslations.length - 1] = { text, tag: tag || 'partial', timestamp: new Date(), isFinal: false, durationMs, speaker: finalSpeaker };
                            } else {
                                newTranslations.push({ text, tag: tag || 'partial', timestamp: new Date(), isFinal: false, durationMs, speaker: finalSpeaker });
                            }
                            return newTranslations;
                        });
                    } else if (showPartial) {
                        setCurrentPartial(text);
                    }
                },
                onAudioReceived: async (audioData) => {
                    if (enableTts) {
                        try {
                            if (audioService.audioContext?.state === 'suspended') {
                                await audioService.audioContext.resume();
                            }
                            await audioService.playAudioChunk(audioData);
                        } catch (error) {
                            console.log('Audio playback error:', error);
                            setNeedsAudioPermission(true);
                        }
                    }
                },
                onSessionCompleted: async () => {
                    console.log('[Receiver] Session completed by host');

                    // CRITICAL: Mark session as terminated by host to prevent ANY reconnection
                    setSessionTerminatedByHost(true);

                    // CRITICAL: Stop the connection first to prevent any reconnection attempts
                    if (signalRService.connection) {
                        try {
                            // Stop the connection completely before cleaning up state
                            await signalRService.connection.stop();
                            console.log('[Receiver] SignalR connection stopped');
                        } catch (error) {
                            console.warn('[Receiver] Error stopping connection:', error);
                        }
                    }

                    // Now disconnect properly with intentional flag
                    await signalRService.disconnect(true);

                    // Clean up connection state completely
                    setIsConnected(false);
                    setIsReconnecting(false);
                    setConnectionLost(false);
                    setIsSessionPaused(false);

                    // Clear transcripts for fresh start
                    // Do NOT clear transcripts here - allow user to download them
                    // setTranslations([]);
                    // setCurrentPartial('');
                    // setUnifiedTranslations([]);
                    // setWaitingForFinal(false);
                    // setLastPartialTime(null);

                    // Reset audio if TTS was enabled
                    if (enableTts) {
                        audioService.reset();
                    }

                    // Update status message - make it clear they need host to restart
                    setStatusMessage('The host has ended this session. Please wait for the host to start a new session.');
                    setStatusType('warning');

                    // Show settings panel so user can see the status
                    setShowSettings(true);

                    // IMPORTANT: Disable autojoin to prevent automatic reconnection attempts
                    setAutojoin(false);
                },
                onSessionPaused: () => {
                    setIsSessionPaused(true);
                    setStatusMessage('Session paused by host');
                    setStatusType('warning');
                },
                onSessionResumed: () => {
                    setIsSessionPaused(false);
                    setStatusMessage('Session resumed');
                    setStatusType('success');
                },
                onSpeechActivity: (speaking) => {
                    setIsSpeaking(speaking);
                },
                onReconnecting: () => {
                    setIsReconnecting(true);
                    setConnectionLost(false);
                    setIsConnected(false);
                    setStatusMessage('Reconnecting...');
                    setStatusType('info');
                },
                onReconnected: () => {
                    setIsReconnecting(false);
                    setConnectionLost(false);
                    setIsConnected(true);
                    setStatusMessage('Reconnected!');
                    setStatusType('success');
                },
                onConnectionStateChanged: (connected, message) => {
                    setIsConnected(connected);
                    if (!connected) {
                        setIsReconnecting(false);
                        setConnectionLost(true);
                        // Reset speaking state on any disconnect — prevents stale animation
                        // if server drops before sending SpeechActivity(false)
                        setIsSpeaking(false);
                    } else {
                        setConnectionLost(false);
                    }
                    setStatusMessage(message);
                    setStatusType(connected ? 'success' : 'error');
                }
            });

            setIsConnected(true);
            setIsReconnecting(false);
            setConnectionLost(false);
            setStatusMessage('Connected!');
            setStatusType('success');

            // Only clear transcripts if connecting to a DIFFERENT session
            if (lastConnectedSessionIdRef.current !== sessionId) {
                console.log(`[Receiver] New session ID (${sessionId}) detected. Clearing previous transcript.`);
                setTranslations([]);
                setUnifiedTranslations([]);
                // Update track for next time
                lastConnectedSessionIdRef.current = sessionId;
            } else {
                console.log(`[Receiver] Reconnecting to same session ID (${sessionId}). Preserving transcript history.`);
            }

            // Always reset partial state
            setCurrentPartial('');
            setWaitingForFinal(false);
            setLastPartialTime(null);
            setShowSettings(false);

        } catch (error) {
            let errorMessage = 'Connection failed';

            // Check browser console for detailed error
            console.error('Connection error:', error);

            // Check for session-related errors first
            if (error.message?.includes('does not exist or is not active') ||
                error.message?.includes('session') ||
                error.response?.data?.includes?.('session')) {
                setSessionNotFound(true);
                errorMessage = `No active session found for ID: "${sessionId}"\n\n` +
                    'This could mean:\n' +
                    '• The host hasn\'t started the session yet\n' +
                    '• The session ID is incorrect\n' +
                    '• The session has expired or ended\n\n' +
                    'Please check with your host to verify:\n' +
                    '1. The correct session ID\n' +
                    '2. That they have started the session';
            } else if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
                errorMessage = `🚫 Network Error connecting to ${signalRService.functionsUrl}\n` +
                    'Possible causes:\n' +
                    '• CORS not configured for localhost:3000\n' +
                    '• Azure Functions not running on localhost:7071\n' +
                    '• Network/firewall blocking the connection\n' +
                    'Check browser console for details.';
            } else if (error.response?.status === 0) {
                errorMessage = '🚫 CORS Error: The Azure Functions app needs to allow http://localhost:3000. ' +
                    'Add this origin to CORS settings in Azure Portal or local.settings.json';
            } else if (error.response?.status === 404) {
                // Check if it's specifically the session that's not found
                if (error.config?.url?.includes('subscribe') || error.config?.url?.includes('add-language')) {
                    setSessionNotFound(true);
                    errorMessage = `Session "${sessionId}" not found.\n\n` +
                        'The session does not exist or hasn\'t been started by a host yet.\n' +
                        'Please verify the session ID with your host.';
                } else {
                    errorMessage = '❌ Azure Function not found. Please check:\n' +
                        '• Functions are deployed/running\n' +
                        '• URL is correct: ' + signalRService.functionsUrl;
                }
            } else if (error.response?.status === 401 || error.response?.status === 403) {
                errorMessage = '🔑 Authentication failed. Please check your function key.';
            } else if (error.message.includes('negotiate')) {
                errorMessage = '🔌 SignalR negotiation failed. Please check the Azure Functions configuration.';
            } else if (error.message.includes('url') || error.message.includes('URL')) {
                errorMessage = '🔌 SignalR connection failed. The negotiate endpoint didn\'t return valid connection info.\n' +
                    'This might mean the Azure Functions are not properly configured.';
            } else {
                errorMessage = `❌ Connection failed: ${error.message}`;
            }

            trackException(error, { sessionId, errorType: 'TranslationReceiver_SignalRConnectionFailed' });
            setStatusMessage(errorMessage);
            setStatusType('error');
            setIsConnected(false);
            setIsReconnecting(false);
            setConnectionLost(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            // CRITICAL: Stop the connection first to prevent any reconnection attempts
            if (signalRService.connection) {
                try {
                    await signalRService.connection.stop();
                    console.log('[Receiver] SignalR connection stopped by user');
                } catch (error) {
                    console.warn('[Receiver] Error stopping connection:', error);
                }
            }

            // Use intentional disconnect to clear all state
            await signalRService.disconnect(true);
            if (enableTts) audioService.reset();

            // Clean up all connection state
            setIsConnected(false);
            setIsReconnecting(false);
            setConnectionLost(false);
            setIsSessionPaused(false);
            setIsSpeaking(false);

            // Clear transcripts
            // Do NOT clear transcripts here - allow user to download them
            // setTranslations([]);
            // setCurrentPartial('');
            // setUnifiedTranslations([]);

            // Update status
            setStatusMessage('Disconnected from session');
            setStatusType('info');
            setShowSettings(true);

            // IMPORTANT: Disable autojoin to prevent automatic reconnection
            setAutojoin(false);
        } catch (error) {
            console.error('[Receiver] Error during disconnect:', error);
            setStatusMessage('Error disconnecting from session');
            setStatusType('error');
        }
    };

    const enableAudio = async () => {
        try {
            if (audioService.audioContext?.state === 'suspended') {
                await audioService.audioContext.resume();
                if (!audioService.keepAudioAlive) audioService.startSilentAudio();
                setNeedsAudioPermission(false);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Helper: Download Transcript
    const downloadTranscript = () => {
        const dataToDownload = unifiedTranslations.length > 0 ? unifiedTranslations : translations;

        if (dataToDownload.length === 0) {
            // Use browser alert or console since we don't have addLog here (or it's internal)
            // But we do have statusMessage which users can see
            setStatusMessage('No transcript data to download');
            setStatusType('warning');
            return;
        }

        // CSV Header
        let csvContent = "Timestamp,Speaker,Source Text,Translated Text\n";

        // CSV Rows
        dataToDownload.forEach(item => {
            if (!item.isFinal && !item.text) return; // Skip empty partials

            const timestamp = item.timestamp ? new Date(item.timestamp).toLocaleTimeString() : '';
            const speaker = item.speaker || 'Unknown';
            // Escape quotes in text by doubling them, wrap fields in quotes
            const sourceText = `"${(item.sourceText || '').replace(/"/g, '""')}"`;
            const translatedText = `"${(item.text || '').replace(/"/g, '""')}"`;

            csvContent += `${timestamp},${speaker},${sourceText},${translatedText}\n`;
        });

        // Create Blob with BOM for UTF-8 support
        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        // Trigger Download
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `transcript_${sessionId}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setStatusMessage('Transcript downloaded successfully');
        setStatusType('success');
    };

    return (
        <div className="receiver-page">
            {/* Status Banner */}
            {/* Status Banner */}
            <div className={`status-banner ${statusType} ${needsAudioPermission ? 'has-action' : ''} ${!showSuccessBanner && statusType === 'success' ? 'fade-out' : ''}`}>
                <div className="status-content">
                    {statusType === 'success' && <CheckCircle size={18} />}
                    {statusType === 'error' && <AlertCircle size={18} />}
                    {statusType === 'warning' && <AlertCircle size={18} />}
                    {statusType === 'info' && <Wifi size={18} />}
                    <span>{statusMessage}</span>
                    {isSignalRDegraded && isConnected && (
                        <span className="degraded-badge" title="Live preview disabled due to network conditions">
                            <AlertCircle size={14} />
                            Preview Off
                        </span>
                    )}
                </div>
                {needsAudioPermission && (
                    <button className="status-action-btn" onClick={enableAudio}>
                        Enable Audio
                    </button>
                )}
            </div>

            {/* Main Content */}
            <div className="receiver-container">
                {/* Settings Panel */}
                <div className={`settings-panel ${showSettings ? 'visible' : 'hidden'}`}>
                    <Card glass className="settings-card">
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <h2>Session Settings</h2>
                                <div className="flex gap-2">
                                    {(translations.length > 0 || unifiedTranslations.length > 0) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={downloadTranscript}
                                            title="Download Transcript"
                                            className="text-blue-400 hover:text-blue-300"
                                        >
                                            <Download size={18} />
                                        </Button>
                                    )}
                                    <div className="hidden sm:block">
                                        <Button variant="ghost" size="sm" onClick={() => setIsSettingsContentCollapsed(!isSettingsContentCollapsed)}>
                                            {isSettingsContentCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                                        </Button>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                                        <X size={18} />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        {!isSettingsContentCollapsed && (
                            <CardBody>
                                <div className="settings-grid">
                                    <Input
                                        label="Session ID"
                                        value={sessionId}
                                        onChange={(e) => {
                                            setSessionId(e.target.value);
                                            // Reset error states when user changes session ID
                                            // This allows connecting to a different session
                                            if (sessionTerminatedByHost || sessionNotFound) {
                                                setSessionTerminatedByHost(false);
                                                setSessionNotFound(false);
                                                setStatusMessage('Enter session details to join');
                                                setStatusType('info');
                                            }
                                        }}
                                        placeholder="Enter Session ID"
                                        disabled={isConnected}
                                    />

                                    <Input
                                        label="Session Password (Optional)"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={isConnected}
                                        icon={<Lock size={16} />}
                                    />

                                    <div className="form-group">
                                        <label className="ui-input-label">Target Language</label>
                                        <div className="select-wrapper">
                                            <Globe className="select-icon" size={18} />
                                            <select
                                                className="ui-select"
                                                value={targetLanguage}
                                                onChange={(e) => setTargetLanguage(e.target.value)}
                                                disabled={isConnected}
                                            >
                                                {LANGUAGES.map(lang => (
                                                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                                                ))}
                                            </select>
                                            <ChevronDown className="select-arrow" size={18} />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="ui-input-label">Font Size: {fontSize}px</label>
                                        <input
                                            type="range"
                                            min="12"
                                            max="42"
                                            value={fontSize}
                                            onChange={(e) => {
                                                const newSize = parseInt(e.target.value);
                                                setFontSize(newSize);
                                                localStorage.setItem('translationFontSize', newSize.toString());
                                            }}
                                            className="ui-range"
                                        />
                                    </div>

                                    <div className="toggles-grid">
                                        <label className="toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={enableTts}
                                                onChange={(e) => setEnableTts(e.target.checked)}
                                                disabled={isConnected}
                                            />
                                            <span className="toggle-text">
                                                {enableTts ? <Volume2 size={18} /> : <VolumeX size={18} />}
                                                AI Spoken Interpretation
                                            </span>
                                        </label>

                                        <label className="toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={showSourceText}
                                                onChange={(e) => {
                                                    setShowSourceText(e.target.checked);
                                                    localStorage.setItem('showSourceText', e.target.checked);
                                                }}
                                            />
                                            <span className="toggle-text">
                                                <FileText size={18} />
                                                Show Original
                                            </span>
                                        </label>

                                        <label className="toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={showPartial}
                                                onChange={(e) => setShowPartial(e.target.checked)}
                                                disabled={isConnected}
                                            />
                                            <span className="toggle-text">
                                                <Type size={18} />
                                                Display Instant Transcription
                                            </span>
                                        </label>
                                    </div>
                                </div>

                            </CardBody>
                        )
                        }
                        <CardFooter>
                            {!isConnected ? (
                                <Button
                                    variant="primary"
                                    className="w-full"
                                    onClick={handleConnect}
                                    disabled={!sessionId}
                                >
                                    Join Session
                                </Button>
                            ) : (
                                <Button
                                    variant="danger"
                                    className="w-full"
                                    onClick={handleDisconnect}
                                >
                                    Leave Session
                                </Button>
                            )}
                        </CardFooter>
                    </Card>
                </div>

                {/* Transcript Area */}
                <div className={`transcript-area ${!showSettings ? 'expanded' : ''}`}>
                    {!showSettings && (
                        <div className="transcript-header">
                            <div className="header-info">
                                <Button variant="ghost" size="sm" onClick={() => {
                                    setShowSettings(true);
                                    setIsSettingsContentCollapsed(false);
                                }}>
                                    <Settings size={18} />
                                </Button>
                                {isConnected && (
                                    <div className="status-indicator-container">
                                        <div className={`status-dot ${isSessionPaused ? 'paused' : 'connected'}`}></div>
                                        <span className="status-text">{isSessionPaused ? 'PAUSED' : 'LIVE'}</span>
                                    </div>
                                )}
                                <span className="session-badge">ID: {sessionId}</span>
                                <span className="lang-badge">{LANGUAGES.find(l => l.code === targetLanguage)?.name || targetLanguage}</span>
                            </div>
                            <div className="header-actions">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    icon={<Download size={14} />}
                                    onClick={downloadTranscript}
                                    title="Download Transcript"
                                    className="mr-2"
                                >
                                    <span className="hidden sm:inline">Download</span>
                                </Button>
                                {isConnected && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={<Copy size={14} />}
                                        onClick={() => {
                                            const baseUrl = window.location.origin;
                                            const meetingLink = `${baseUrl}/receiver?sessionId=${encodeURIComponent(sessionId)}&targetLanguage=${encodeURIComponent(targetLanguage)}&enableTts=${enableTts}&showPartial=${showPartial}&autojoin=true`;
                                            navigator.clipboard.writeText(meetingLink);
                                            console.log('Meeting link copied:', meetingLink);
                                        }}
                                    >
                                        <span className="hidden sm:inline">Copy Link</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    <div
                        className="transcript-content"
                        ref={translationOutputRef}
                        style={{ fontSize: `${fontSize}px` }}
                    >
                        {/* Not connected yet or Disconnected - Only show full empty state if NO translations */}
                        {!isConnected && !isReconnecting && translations.length === 0 && unifiedTranslations.length === 0 && (
                            <div className="empty-state">
                                {isPostSession ? (
                                    <>
                                        <div className="empty-icon">
                                            <FileText size={48} />
                                        </div>
                                        <h3>Session Ended - Content Available</h3>
                                        <p>This session has ended. You can still access the transcript and AI features.</p>
                                        {postSessionData && postSessionData.sourceLanguage && (
                                            <p className="post-session-meta">
                                                Source: {postSessionData.sourceLanguage}
                                                {postSessionData.segmentCount > 0 && ` \u2022 ${postSessionData.segmentCount} segments`}
                                            </p>
                                        )}
                                        <div className="post-session-actions">
                                            <a
                                                href={getAiSummaryUrl()}
                                                className="post-session-link"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-ai-id="post-session-transcript-link"
                                                data-ai-action="click"
                                                data-ai-feature="post-session-access"
                                            >
                                                <FileText size={16} />
                                                View Transcript &amp; AI Summary
                                            </a>
                                        </div>
                                    </>
                                ) : sessionTerminatedByHost ? (
                                    <>
                                        <div className="empty-icon">
                                            <AlertCircle size={48} />
                                        </div>
                                        <h3>Session Ended by Host</h3>
                                        <p>The host has ended this session.</p>
                                        <p style={{ marginTop: '10px' }}>If the host has restarted the session, click "Try Reconnecting" below.</p>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={handleConnect}
                                            style={{ marginTop: '15px' }}
                                        >
                                            Try Reconnecting
                                        </Button>
                                        {sessionId && (
                                            <div className="ai-summary-note">
                                                <span className="ai-summary-note-icon" aria-hidden="true">
                                                    <Bot size={16} />
                                                </span>
                                                <span className="ai-summary-note-text">
                                                    AI summary available —{' '}
                                                    <a
                                                        href={getAiSummaryUrl()}
                                                        className="ai-summary-note-link"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-ai-id="post-session-ai-summary-link"
                                                        data-ai-action="click"
                                                        data-ai-feature="session-ended"
                                                    >
                                                        View AI Summary
                                                    </a>
                                                </span>
                                            </div>
                                        )}
                                    </>
                                ) : sessionNotFound ? (
                                    <>
                                        <div className="empty-icon">
                                            <AlertCircle size={48} />
                                        </div>
                                        <h3>Session Not Found</h3>
                                        <p>No active session found with ID: "{sessionId}"</p>
                                        <p style={{ marginTop: '10px' }}>Please check with your host to verify the correct session ID and ensure they have started the session.</p>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={handleConnect}
                                            style={{ marginTop: '15px' }}
                                        >
                                            Try Again
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="empty-icon">
                                            <Settings size={48} />
                                        </div>
                                        <h3>Ready to join</h3>
                                        <p>Enter your session details and click "Join Session" to start receiving translations.</p>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Connected but waiting for translations */}
                        {isConnected && translations.length === 0 && unifiedTranslations.length === 0 && !currentPartial && (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <RefreshCw size={48} />
                                </div>
                                <h3>Waiting for translations...</h3>
                                <p>Translations will appear here once the host starts speaking.</p>
                            </div>
                        )}

                        {/* Unified Mode Rendering */}
                        {canUnifyModel && unifiedTranslations.map((t, index) => {
                            // Show speaker badge when speaker changes from previous message
                            const prevSpeaker = index > 0 ? unifiedTranslations[index - 1].speaker : null;
                            const showSpeakerBadge = t.speaker && t.speaker !== prevSpeaker;
                            // Cycle through colors based on speaker number (S1, S2, etc.)
                            const speakerNum = t.speaker ? parseInt(t.speaker.replace(/\D/g, '')) || 1 : 1;
                            const speakerColorClass = `speaker-${((speakerNum - 1) % 3) + 1}`;

                            return (
                                <div key={index} className={`message-bubble ${t.isFinal ? 'final' : 'partial'} ${t.isFallbackFromPartial ? 'fallback-from-partial' : ''}`}>
                                    {showSpeakerBadge && (
                                        <div className={`speaker-badge ${speakerColorClass}`}>
                                            {t.speaker ? t.speaker.replace(/^(?:S|Speaker\s*)?(\d+)$/i, 'Speaker $1') : t.speaker}
                                        </div>
                                    )}
                                    {t.isFinal && showSourceText && t.sourceText && (
                                        <div className="source-text-container">
                                            <span className="source-text-label">Original</span>
                                            <div className="source-text">{t.sourceText}</div>
                                        </div>
                                    )}
                                    <div className="message-text">{renderWithGlossaryHighlights(t.text)}</div>
                                    {t.isFinal ? (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div className="message-time">{t.timestamp.toLocaleTimeString()}{t.tag && <span className="model-tag"> • {t.tag}</span>}{t.durationMs > 0 && <span className="duration-tag"> • {t.durationMs}ms</span>}</div>
                                            {t.isFallbackFromPartial && (
                                                <span className="fallback-tag">
                                                    Fallback-Partial
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="message-time">{t.tag && <span className="model-tag">{t.tag}</span>}{t.durationMs > 0 && <span className="duration-tag"> • {t.durationMs}ms</span>}</div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Traditional Mode Rendering */}
                        {!canUnifyModel && translations.map((t, index) => {
                            // Show speaker badge when speaker changes from previous message
                            const prevSpeaker = index > 0 ? translations[index - 1].speaker : null;
                            const showSpeakerBadge = t.speaker && t.speaker !== prevSpeaker;
                            // Cycle through colors based on speaker number (S1, S2, etc.)
                            const speakerNum = t.speaker ? parseInt(t.speaker.replace(/\D/g, '')) || 1 : 1;
                            const speakerColorClass = `speaker-${((speakerNum - 1) % 3) + 1}`;

                            return (
                                <div key={index} className={`message-bubble final ${t.isFallbackFromPartial ? 'fallback-from-partial' : ''}`}>
                                    {showSpeakerBadge && (
                                        <div className={`speaker-badge ${speakerColorClass}`}>
                                            {t.speaker ? t.speaker.replace(/^(?:S|Speaker\s*)?(\d+)$/i, 'Speaker $1') : t.speaker}
                                        </div>
                                    )}
                                    {showSourceText && t.sourceText && (
                                        <div className="source-text-container">
                                            <span className="source-text-label">Original</span>
                                            <div className="source-text">{t.sourceText}</div>
                                        </div>
                                    )}
                                    <div className="message-text">{renderWithGlossaryHighlights(t.text)}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div className="message-time">{t.timestamp.toLocaleTimeString()}{t.tag && <span className="model-tag"> • {t.tag}</span>}{t.durationMs > 0 && <span className="duration-tag"> • {t.durationMs}ms</span>}</div>
                                        {t.isFallbackFromPartial && (
                                            <span className="fallback-tag">
                                                Fallback-Partial
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Partial Text (Traditional Mode) */}
                        {!canUnifyModel && (showPartial && currentPartial) && (
                            <div className="message-bubble partial">
                                <div className="message-text">{renderWithGlossaryHighlights(currentPartial)}</div>
                            </div>
                        )}

                        {/* Waiting for Final Indicator - shows when partial received but final is delayed */}
                        {waitingForFinal && lastPartialTime && (Date.now() - lastPartialTime > 5000) && (
                            <div className="delayed-warning">
                                ⏱️ Translating final (delayed)...
                            </div>
                        )}

                        {/* Loading Dots */}
                        {loadingDots && (
                            <div className="loading-indicator">
                                Translating{loadingDots}
                            </div>
                        )}

                        {/* Paused Indicator */}
                        {isSessionPaused && (
                            <div className="paused-indicator">
                                <Pause size={24} />
                                <span>Session Paused</span>
                            </div>
                        )}
                        <div ref={messagesEndRef} style={{ float: "left", clear: "both" }} />

                        {/* Session Ended Footer - Show when disconnected but we have content */}
                        {!isConnected && !isReconnecting && (translations.length > 0 || unifiedTranslations.length > 0) && sessionTerminatedByHost && (
                            <div className="session-ended-footer">
                                <h4>Session Ended</h4>
                                <p>The host has ended this session. You can review the transcript above.</p>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleConnect}
                                >
                                    Try Reconnecting
                                </Button>
                                {sessionId && (
                                    <div className="ai-summary-note">
                                        <span className="ai-summary-note-icon" aria-hidden="true">
                                            <Bot size={16} />
                                        </span>
                                        <span className="ai-summary-note-text">
                                            AI summary available —{' '}
                                            <a
                                                href={getAiSummaryUrl()}
                                                className="ai-summary-note-link"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-ai-id="post-session-ai-summary-link-footer"
                                                data-ai-action="click"
                                                data-ai-feature="session-ended"
                                            >
                                                View AI Summary
                                            </a>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TranslationReceiverRevised;
